import {LoadingSection} from '#/atoms/loading-section';
import {WishlistButton} from '#/components/buttons/wishlist-button';
import {ReactComponent as IconBack} from '#/components/icons/ico-back.svg';
import {LandMarketIndicator} from '#/components/indicators/market-indicator';
import {TransLandCityStateText} from '#/components/land/land-city-state-text';
import {landHasMarket} from '#/utils/land-utils';
import {removeDoubleAndStartingSpaces} from '#/utils/string-utils';
import {XMarkIcon} from '@heroicons/react/24/solid';
import {Land, LandMarketInfo, UnlistedLand} from '@shareland/shareland-common';
import {ComponentPropsWithoutRef, FunctionComponent, PropsWithChildren} from 'react';

type ActionMenuHeaderProps = PropsWithChildren<{
  land: Land | UnlistedLand;
  landMarketInfo?: LandMarketInfo;
}>;

export const ActionMenuHeader: FunctionComponent<ActionMenuHeaderProps> = ({children, land, landMarketInfo}) => {
  const isListed = landHasMarket(land);

  return (
    <div className='border-b border-white/20 pb-3'>
      <div className='flex justify-between items-center gap-3'>
        <span className='grow text-white font-bold leading-4 text-2xl'>{land.name}</span>
        {isListed && <WishlistButton land={land} landMarketInfo={landMarketInfo} />}
        {children}
      </div>

      <div className='flex justify-between items-center gap-3'>
        <span className='text-gray-100 font-semibold text-sm'>
          <TransLandCityStateText land={land} />
        </span>
      </div>

      {isListed && landMarketInfo && (
        <div className='flex justify-between items-center gap-3 mt-0.5'>
          <LandMarketIndicator
            className='text-gray-300 font-semibold text-xs'
            land={land}
            range='daily'
            landMarketInfo={landMarketInfo}
            iconSize='xs'
            showDifference={false}
          />
        </div>
      )}
    </div>
  );
};

export const ActionMenuHeaderLoading: FunctionComponent = () => {
  return (
    <div className='border-b border-white/20 pb-3'>
      <LoadingSection w='w-28' h='h-[20px] my-[4px]' animate />
      <LoadingSection w='w-20' h='h-[16px] my-[2px] mb-0.5' animate />
    </div>
  );
};

interface Props extends ComponentPropsWithoutRef<'button'> {
  className?: string;
  onClick?: () => void;
  variant?: 'regular' | 'hollow';
}
export const ActionMenuHeaderButton: FunctionComponent<Props> = ({className, children, onClick, ...rest}) => {
  const buttonClassName = removeDoubleAndStartingSpaces(`
  flex
  justify-center
  items-center
  w-7
  h-7
  p-0.5
  border
  border-white
  rounded-full
  hover:opacity-70
  ${className ?? ''}
  `);

  return (
    <button onClick={onClick} className={buttonClassName} {...rest}>
      {children}
    </button>
  );
};

export const ActionMenuHeaderIcons = {
  Close: () => <XMarkIcon className='w-7 h-7 fill-white stroke-transparent' />,
  Back: () => <IconBack className='w-8 h-8' />,
};
